import {
  VideoCameraIcon,
  MicrophoneIcon,
  MapPinIcon,
  ChevronRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogSeparator,
  DialogFooter,
} from "./ui/dialog";
import { APP_FONT_STACK } from "../utils/common";
import PermissionHelpDialog from "./PermissionHelpDialog.jsx";
import usePermissionChecklist from "../hooks/usePermissionChecklist";

/**
 * "Before you begin" — pre-call permission checklist (Figma: Pre Call, node
 * 2070-616). Mobile-first, purely presentational: all camera / microphone /
 * location permission logic lives in usePermissionChecklist().
 *
 * Each row is binary: granted → green "Access granted"; not granted → "How to
 * enable" (opens manual guidance). All three are required for "Continue to
 * Call".
 */

// --- status pills -----------------------------------------------------------
const GreenPill = ({ children }) => (
  <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-green-150/10 px-3 py-1 text-xs font-semibold text-green-250 sm:text-sm">
    <CheckCircleIcon className="h-4 w-4" />
    {children}
  </span>
);

const GrayPill = ({ children }) => (
  <span className="flex items-center gap-2 whitespace-nowrap rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-150 sm:text-sm">
    <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
    {children}
  </span>
);

const HowToEnableButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="whitespace-nowrap rounded-lg bg-orange-150 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-150/90 sm:px-4 sm:text-sm"
  >
    {label}
  </button>
);

const ChecklistRow = ({ Icon, title, description, status }) => (
  <div className="rounded-xl border border-gray-275 p-3 sm:p-4">
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-150/10 sm:h-10 sm:w-10">
        <Icon className="h-5 w-5 text-orange-150" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="mb-0 text-base font-semibold text-black-150 sm:text-lg">
            {title}
          </p>
          <div className="shrink-0">{status}</div>
        </div>
        <p className="mb-0 mt-1 text-xs text-gray-150 sm:text-sm">
          {description}
        </p>
      </div>
    </div>
  </div>
);

export default function PreCallCheckModal({
  open,
  onClose,
  onContinue,
  selectedLang,
}) {
  const { t } = useTranslation();
  const { perm, helpOpen, openHelp, closeHelp, allGranted } =
    usePermissionChecklist(open);

  // Camera / Microphone / Location status:
  //   granted     → green "Access granted"
  //   not granted → "How to enable" → manual guidance dialog
  //   checking    → spinner
  const permissionStatus = (type) => {
    const allowed = perm[type];
    if (allowed) return <GreenPill>{t("Access granted")}</GreenPill>;
    if (allowed === false)
      return <HowToEnableButton label={t("How to enable")} onClick={openHelp} />;
    return <GrayPill>{t("Checking")}</GrayPill>;
  };

  const fontFamily = APP_FONT_STACK;
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && onClose) onClose();
        }}
      >
        <DialogContent
          maxWidth="max-w-lg"
          style={{ fontFamily }}
          // The permission checklist is a gate — don't let an accidental
          // backdrop click dismiss it. (The × and Escape still close it.)
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("Before you begin")}</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <p className="mb-0 mt-3 text-sm text-gray-150 sm:text-base">
              {t(
                "Please allow the following permissions to complete your video verification.",
              )}
            </p>

            <div className="mt-4 flex flex-col gap-3">
              <ChecklistRow
                Icon={VideoCameraIcon}
                title={t("Camera")}
                description={t(
                  "Required to verify your identity during the video call.",
                )}
                status={permissionStatus("camera")}
              />
              <ChecklistRow
                Icon={MicrophoneIcon}
                title={t("Microphone")}
                description={t(
                  "Required for clear communication during the verification.",
                )}
                status={permissionStatus("microphone")}
              />
              <ChecklistRow
                Icon={MapPinIcon}
                title={t("Location")}
                description={t("Required to verify your location for session.")}
                status={permissionStatus("location")}
              />
            </div>
          </DialogBody>

          <DialogSeparator className="mt-4" />

          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={onContinue}
              disabled={!allGranted}
              className="flex w-full items-center justify-center gap-1 rounded-lg bg-orange-150 px-5 py-2.5 text-base font-semibold text-white hover:bg-orange-150/90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {t("Continue to Call")}
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PermissionHelpDialog
        open={open && helpOpen}
        onClose={closeHelp}
        selectedLang={selectedLang}
      />
    </>
  );
}
